// User collections: save mined-event video snippets into named collections (plus a default Queue),
// and track a view history. Scoped per user (userId field), consistent with the events collection.
// Queries use single-field filters (auto-indexed) with client-side sort/dedupe — no composite
// indexes needed.
import { db } from './firebase';
import { getAuth } from 'firebase/auth';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';

export interface Snippet {
  eventId?: string;
  videoId?: string;
  title?: string;
  summary?: string;
  channelId?: string;
  channelTitle?: string;
  sourceTimestampSec?: number;
  sourceTimestampUrl?: string;
  eventDateKey?: string;
}

export interface UserCollection { id: string; name: string; kind: 'queue' | 'custom' }
export interface SavedItem extends Snippet { id: string; collectionId?: string; addedAt?: any; viewedAt?: any }

const QUEUE_NAME = 'Queue';
const uid = (): string | null => getAuth().currentUser?.uid ?? null;
const clean = (s: Snippet): Snippet => Object.fromEntries(Object.entries(s).filter(([, v]) => v !== undefined)) as Snippet;

export async function listCollections(): Promise<UserCollection[]> {
  const u = uid(); if (!u || !db) return [];
  const snap = await getDocs(query(collection(db, 'collections'), where('userId', '==', u)));
  const out = snap.docs.map((d) => ({ id: d.id, name: (d.data() as any).name || 'Untitled', kind: ((d.data() as any).kind || 'custom') as 'queue' | 'custom' }));
  return out.sort((a, b) => (a.kind === 'queue' ? -1 : b.kind === 'queue' ? 1 : a.name.localeCompare(b.name)));
}

export async function ensureQueue(): Promise<string> {
  const u = uid(); if (!u || !db) throw new Error('Sign in to save.');
  const snap = await getDocs(query(collection(db, 'collections'), where('userId', '==', u)));
  const existing = snap.docs.find((d) => (d.data() as any).kind === 'queue');
  if (existing) return existing.id;
  const ref = await addDoc(collection(db, 'collections'), { userId: u, name: QUEUE_NAME, kind: 'queue', createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  return ref.id;
}

export async function createCollection(name: string): Promise<string> {
  const u = uid(); if (!u || !db) throw new Error('Sign in to create a collection.');
  const ref = await addDoc(collection(db, 'collections'), { userId: u, name: name.trim() || 'Untitled', kind: 'custom', createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  return ref.id;
}

export async function renameCollection(id: string, name: string): Promise<void> {
  if (!db) return;
  await updateDoc(doc(db, 'collections', id), { name: name.trim() || 'Untitled', updatedAt: serverTimestamp() });
}

export async function deleteCollection(id: string): Promise<void> {
  if (!db) return;
  const items = await getDocs(query(collection(db, 'collectionItems'), where('collectionId', '==', id)));
  await Promise.all(items.docs.map((d) => deleteDoc(d.ref)));
  await deleteDoc(doc(db, 'collections', id));
}

// Add a snippet to a collection (deduped by eventId within the collection).
export async function addToCollection(collectionId: string, snippet: Snippet): Promise<'added' | 'exists'> {
  const u = uid(); if (!u || !db) throw new Error('Sign in to save.');
  if (snippet.eventId) {
    const existing = await getDocs(query(collection(db, 'collectionItems'), where('collectionId', '==', collectionId)));
    if (existing.docs.some((d) => (d.data() as any).eventId === snippet.eventId)) return 'exists';
  }
  await addDoc(collection(db, 'collectionItems'), { userId: u, collectionId, ...clean(snippet), addedAt: serverTimestamp() });
  return 'added';
}

export async function addToQueue(snippet: Snippet): Promise<'added' | 'exists'> {
  return addToCollection(await ensureQueue(), snippet);
}

export async function listItems(collectionId: string): Promise<SavedItem[]> {
  if (!db) return [];
  const snap = await getDocs(query(collection(db, 'collectionItems'), where('collectionId', '==', collectionId)));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }) as SavedItem).sort((a, b) => (b.addedAt?.seconds || 0) - (a.addedAt?.seconds || 0));
}

export async function removeItem(id: string): Promise<void> {
  if (!db) return;
  await deleteDoc(doc(db, 'collectionItems', id));
}

// --- history ---
export async function recordHistory(snippet: Snippet): Promise<void> {
  const u = uid(); if (!u || !db) return;
  try { await addDoc(collection(db, 'viewHistory'), { userId: u, ...clean(snippet), viewedAt: serverTimestamp() }); } catch { /* best-effort */ }
}

export async function listHistory(max = 100): Promise<SavedItem[]> {
  const u = uid(); if (!u || !db) return [];
  const snap = await getDocs(query(collection(db, 'viewHistory'), where('userId', '==', u)));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }) as SavedItem).sort((a, b) => (b.viewedAt?.seconds || 0) - (a.viewedAt?.seconds || 0)).slice(0, max);
}
